import type { Gate } from '../../../data/types';
import type { Ring } from '../../../geo/types';
import type { Frame, MeshBuilder, VertexStyle } from '../../mesh/meshBuilder';
import { SURFACE } from '../../mesh/attributes';
import { archedBlock } from '../landmarks/fortress/fortressBlocks';
import { boxSolid } from '../solids';
import { gableRoof } from '../roofSolids';
import { defensiveMasonryFace } from './towerMasonry';

export const BLOKHUIS_GATE_ID = 'gate-blokhuis-op-de-dijk';

/** The painted Lastage dike entrance: an open road arch below a narrow defensive tower.
 * Its measured location is reconstructed from the crossing; hidden sides and elevations
 * remain estimates. The former standalone solid tower did not allow the road through. */
export const BLOKHUIS_GATE = {
  passageRatio: 0.675,
  springY: 2.7,
  upperFootY: 5.85,
  eaveY: 12,
  roofRise: 4.8,
  footY: -1.4,
} as const;

export function blokhuisPassageWidth(gate: Gate): number {
  return gate.width * BLOKHUIS_GATE.passageRatio;
}

interface Styles {
  readonly wall: VertexStyle;
  readonly timber: VertexStyle;
  readonly roof: VertexStyle;
  readonly dark: VertexStyle;
}

function upperBody(builder: MeshBuilder, frame: Frame, ring: Ring, styles: Styles): void {
  ring.forEach((a, i) => {
    const b = ring[(i + 1) % ring.length];
    if (!b) return;
    const width = Math.hypot(b[0] - a[0], b[1] - a[1]);
    const windows = [1.1, 3.9].map((y) => ({
      x: width / 2 - 0.25,
      y,
      width: 0.5,
      height: 0.9,
      cuts: [],
    }));
    defensiveMasonryFace(
      builder,
      styles.timber,
      frame,
      a,
      b,
      BLOKHUIS_GATE.upperFootY,
      BLOKHUIS_GATE.eaveY - BLOKHUIS_GATE.upperFootY,
      windows,
      { ...styles.dark, surface: SURFACE.wood }
    );
  });
}

function frameBands(
  builder: MeshBuilder,
  frame: Frame,
  u: number,
  v: number,
  timber: VertexStyle
): void {
  for (const y of [6, 8.6, 11.85]) {
    for (const side of [-1, 1]) {
      boxSolid(builder, timber, frame, [-u, y, side * v - 0.08], [u, y + 0.14, side * v + 0.08]);
      boxSolid(builder, timber, frame, [side * u - 0.08, y, -v], [side * u + 0.08, y + 0.14, v]);
    }
  }
  for (const sideU of [-1, 1])
    for (const sideV of [-1, 1]) {
      const x = sideU * u,
        z = sideV * v;
      boxSolid(
        builder,
        timber,
        frame,
        [x - 0.08, BLOKHUIS_GATE.upperFootY, z - 0.08],
        [x + 0.08, BLOKHUIS_GATE.eaveY, z + 0.08]
      );
    }
}

export function buildBlokhuisGate(
  builder: MeshBuilder,
  frame: Frame,
  gate: Gate,
  styles: Styles
): void {
  const u = gate.depth / 2 - 0.12,
    v = gate.width / 2 - 0.12;
  const passage = blokhuisPassageWidth(gate) / 2;
  archedBlock(builder, styles.wall, frame, {
    u: [-u, u],
    v: [-v, v],
    foot: BLOKHUIS_GATE.footY,
    top: BLOKHUIS_GATE.upperFootY,
    passage: { v: [-passage, passage], springing: BLOKHUIS_GATE.springY },
    openTop: true,
  });
  const ring: Ring = [
    [-u, -v],
    [u, -v],
    [u, v],
    [-u, v],
  ];
  upperBody(builder, frame, ring, styles);
  frameBands(builder, frame, u, v, styles.timber);
  gableRoof(builder, { wall: styles.timber, roof: styles.roof }, frame, {
    u0: -u,
    u1: u,
    v0: -v,
    v1: v,
    eaveY: BLOKHUIS_GATE.eaveY,
    ridgeY: BLOKHUIS_GATE.eaveY + BLOKHUIS_GATE.roofRise,
    ends: { start: true, end: true },
  });
}
